import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "../../../utils/tw";

interface AutocompleteInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> {
  label: string;
  value: string;
  onChange: (val: string) => void;
  options: string[];
  error?: string;
  dropdownRender?: (args: {
    open: boolean;
    filtered: string[];
    active: number;
    selectValue: (val: string) => void;
  }) => React.ReactNode;
}

interface AutocompleteInputExtraProps {
  onSelectOption?: (val: string) => void;
}

const AutocompleteInput = React.forwardRef<HTMLInputElement, AutocompleteInputProps & AutocompleteInputExtraProps>(
  (
    { label, value, onChange, options, error, className, id, required, onSelectOption, dropdownRender, ...props },
    ref
  ) => {
    const inputId = id || props.name || label.toLowerCase().replace(/\s+/g, "-");
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [active, setActive] = useState(-1);
    const [open, setOpen] = useState(false);

    // Memoize options to prevent unnecessary re-renders
    const memoizedOptions = useMemo(() => options, [options.join(',')]);

    // Calculate filtered options without storing in state to avoid re-render loops
    const filtered = useMemo(() => {
      if (!value || value.trim() === '') {
        return [];
      }
      return memoizedOptions
        .filter(o => o.toLowerCase().includes(value.toLowerCase()))
        .slice(0, 10);
    }, [value, memoizedOptions]);

    // Update open state when filtered results change
    useEffect(() => {
      setOpen(filtered.length > 0 && value.trim() !== '');
      setActive(-1);
    }, [filtered.length, value]);    const selectValue = useCallback((val: string) => {
      setOpen(false);
      setActive(-1);
      if (onSelectOption) {
        onSelectOption(val);
      }
    }, [onSelectOption]);

    const handleAdd = useCallback(async (val: string) => {
      setOpen(false);
      setActive(-1);
      if (onSelectOption) {
        onSelectOption(val);
      }
    }, [onSelectOption]);

    useEffect(() => {
      function handleClickOutside(e: MouseEvent) {
        if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
          setOpen(false);
          setActive(-1);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);    const handleKey = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!open) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive(a => Math.min(a + 1, filtered.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive(a => Math.max(a - 1, 0));
      } else if (e.key === "Enter" && active >= 0) {
        e.preventDefault();
        selectValue(filtered[active]);
      }
    }, [open, filtered.length, active, selectValue]);

    const handleFocus = useCallback(() => {
      if (value && filtered.length > 0) {
        setOpen(true);
      }
    }, [value, filtered.length]);

    const handleBlur = useCallback(() => {
      // Delay closing to allow click events on dropdown items
      setTimeout(() => setOpen(false), 150);
    }, []);

    return (
      <div className="relative space-y-1" ref={wrapperRef}>
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            placeholder=" "
            className={cn(
              'peer w-full border border-border-default bg-bg-surface px-3 py-2 rounded-lg outline-none focus:border-ds-primary-500 focus:ring-1 focus:ring-ds-primary-500/30 placeholder-transparent min-h-[48px] text-text-primary',
              error && 'border-border-error focus:border-border-error focus:ring-ds-status-error-main/30',
              className
            )}
            value={value}
            required={required}
            onChange={e => onChange(e.target.value)}
            onKeyDown={handleKey}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...props}
          />
          <label
            htmlFor={inputId}
            className="absolute left-3 top-1 text-xs text-text-muted transition-all peer-focus:-top-2 peer-focus:text-xs peer-focus:text-ds-primary-600 peer-placeholder-shown:top-2.5 peer-placeholder-shown:text-text-muted"
          >
            {label} {required && "*"}
          </label>
        </div>
        {dropdownRender && dropdownRender({ open, filtered, active, selectValue })}
        {error && <p className="text-ds-status-error-main text-xs">{error}</p>}
      </div>
    );
  }
);

AutocompleteInput.displayName = "AutocompleteInput";

export default AutocompleteInput;
